import { Lightbulb, Cloud, ShieldCheck, Code2, BarChart3 } from "lucide-react";
import { useState } from "react";

const services = [
    {
        number: "01",
        icon: Lightbulb,
        title: "IT Strategy & Digital Transformation",
        description: "We help you rethink, redesign, and realign your technology landscape to accelerate innovation and reduce operational friction. Our strategic consulting bridges the gap between business objectives and technical execution.",
        tags: ["Strategy", "Innovation", "Consulting"],
    },
    {
        number: "02",
        icon: Cloud,
        title: "Cloud Solutions & Migration",
        description: "Whether you're moving to the cloud or optimizing your current environment, we design resilient, cost-efficient architectures across leading platforms.",
        tags: ["Amazon Web Services", "Microsoft Azure", "Google Cloud Platform"],
    },
    {
        number: "03",
        icon: ShieldCheck,
        title: "Cybersecurity & Risk Management",
        description: "Cyber threats evolve daily — your defenses should too. We implement proactive security frameworks, compliance strategies, and real-time monitoring to safeguard your digital assets.",
        tags: ["Security", "Compliance", "Monitoring"],
    },
    {
        number: "04",
        icon: Code2,
        title: "Enterprise Application Development",
        description: "From enterprise systems to custom business applications, we build secure, high-performance solutions tailored to your operational needs.",
        tags: ["Enterprise", "Custom Apps", "High Performance"],
    },
    {
        number: "05",
        icon: BarChart3,
        title: "Data & Analytics",
        description: "Data is your most valuable asset — when used correctly. We design intelligent analytics systems that turn raw data into actionable insights.",
        tags: ["Analytics", "Insights", "Intelligence"],
    },
];

export default function WhatWeDo() {
    const [active, setActive] = useState(0);

    return (
        <section className="relative py-12 md:py-24 overflow-hidden">
            <style>{`
                .wwd-tab {
                    transition: all 0.3s ease;
                    position: relative;
                }
                .wwd-tab::before {
                    content: '';
                    position: absolute;
                    left: 0; top: 0;
                    width: 3px; height: 0%;
                    background: linear-gradient(to bottom, #a855f7, #3b82f6);
                    border-radius: 9999px;
                    transition: height 0.4s cubic-bezier(0.4,0,0.2,1);
                }
                .wwd-tab.wwd-active::before { height: 100%; }
                .wwd-tab.wwd-active {
                    background: linear-gradient(135deg, rgba(147,51,234,0.1) 0%, rgba(37,99,235,0.08) 100%) !important;
                    border-color: rgba(147,51,234,0.25) !important;
                }

                @keyframes panelFade {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .panel-fade { animation: panelFade 0.35s cubic-bezier(0.4,0,0.2,1) forwards; }

                .tag-pill {
                    transition: background 0.2s, border-color 0.2s, color 0.2s;
                }
                .tag-pill:hover {
                    background: rgba(147,51,234,0.2) !important;
                    border-color: rgba(147,51,234,0.45) !important;
                    color: rgba(216,180,254,1) !important;
                }
            `}</style>

            {/* Ambient glows */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute top-[20%] left-[5%] w-[400px] h-[300px] rounded-full blur-[120px]"
                    style={{ background: "rgba(147,51,234,0.06)" }} />
                <div className="absolute bottom-[20%] right-[5%] w-[350px] h-[280px] rounded-full blur-[120px]"
                    style={{ background: "rgba(59,130,246,0.06)" }} />
            </div>

            <div className="container relative z-10">

                {/* Header */}
                <div className="mb-10 md:mb-14 text-center">
                    <h2 className="text-2xl md:text-[2.6rem] font-bold text-white leading-snug mb-4">
                        What We Do
                    </h2>
                </div>

                {/* ── Mobile / Tablet: accordion ── */}
                <div className="lg:hidden flex flex-col gap-3">
                    {services.map((item, i) => {
                        const Icon = item.icon;
                        const isActive = active === i;
                        return (
                            <div
                                key={item.number}
                                className="rounded-xl border overflow-hidden"
                                style={{
                                    borderColor: isActive ? "rgba(147,51,234,0.35)" : "rgba(255,255,255,0.07)",
                                    background: isActive
                                        ? "linear-gradient(135deg, rgba(147,51,234,0.1) 0%, rgba(37,99,235,0.08) 100%)"
                                        : "rgba(255,255,255,0.02)",
                                }}
                            >
                                {/* Accordion header */}
                                <button
                                    onClick={() => setActive(isActive ? -1 : i)}
                                    className="w-full text-left px-4 py-4 flex items-center gap-3"
                                >
                                    <div
                                        className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300"
                                        style={{
                                            background: isActive
                                                ? "linear-gradient(135deg, #9333ea, #3b82f6)"
                                                : "rgba(255,255,255,0.05)",
                                            boxShadow: isActive ? "0 4px 16px rgba(147,51,234,0.4)" : "none",
                                        }}
                                    >
                                        <Icon className="w-4 h-4 text-white" />
                                    </div>
                                    <span
                                        className="font-semibold text-sm leading-snug flex-1"
                                        style={{ color: isActive ? "white" : "rgba(255,255,255,0.55)" }}
                                    >
                                        {item.title}
                                    </span>
                                    {/* Chevron */}
                                    <svg
                                        className="shrink-0 w-4 h-4 transition-transform duration-300"
                                        style={{
                                            color: "rgba(255,255,255,0.3)",
                                            transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                                        }}
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {/* Accordion body */}
                                {isActive && (
                                    <div className="panel-fade px-4 pb-5 flex flex-col gap-3">
                                        <div className="w-10 h-0.5 rounded-full bg-gradient-to-r from-purple-500 to-blue-500" />
                                        <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>
                                            {item.description}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {item.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="tag-pill px-3 py-1 rounded-full text-xs font-semibold"
                                                    style={{
                                                        background: "rgba(147,51,234,0.1)",
                                                        border: "1px solid rgba(147,51,234,0.22)",
                                                        color: "rgba(192,132,252,0.85)",
                                                    }}
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── Desktop: original tab + panel layout ── */}
                <div className="hidden lg:grid lg:grid-cols-5 gap-6 items-start">

                    {/* Left: vertical tab list */}
                    <div className="lg:col-span-2 flex flex-col gap-3.5">
                        {services.map((item, i) => {
                            const Icon = item.icon;
                            const isActive = active === i;
                            return (
                                <button
                                    key={item.number}
                                    onClick={() => setActive(i)}
                                    className={`wwd-tab ${isActive ? "wwd-active" : ""} text-left rounded-xl border pl-5 pr-4 py-4 flex items-center gap-4`}
                                    style={{
                                        background: "rgba(255,255,255,0.02)",
                                        borderColor: "rgba(255,255,255,0.07)",
                                    }}
                                >
                                    <div
                                        className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300"
                                        style={{
                                            background: isActive
                                                ? "linear-gradient(135deg, #9333ea, #3b82f6)"
                                                : "rgba(255,255,255,0.05)",
                                            boxShadow: isActive ? "0 4px 16px rgba(147,51,234,0.4)" : "none",
                                        }}
                                    >
                                        <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="min-w-0">
                                        <span
                                            className="font-semibold leading-snug block truncate transition-colors duration-200"
                                            style={{ color: isActive ? "white" : "rgba(255,255,255,0.45)" }}
                                        >
                                            {item.title}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Right: detail panel */}
                    <div className="lg:col-span-3 lg:sticky lg:top-28">
                        {services.map((item, i) => {
                            const Icon = item.icon;
                            if (i !== active) return null;
                            return (
                                <div
                                    key={item.number}
                                    className="panel-fade rounded-2xl border p-8 md:p-10"
                                    style={{
                                        background: "linear-gradient(135deg, rgba(147,51,234,0.09) 0%, rgba(37,99,235,0.09) 100%)",
                                        borderColor: "rgba(147,51,234,0.22)",
                                        boxShadow: "0 0 60px rgba(147,51,234,0.07)",
                                    }}
                                >
                                    <p
                                        className="text-6xl font-black leading-none mb-6 select-none"
                                        style={{
                                            background: "linear-gradient(135deg, rgba(147,51,234,0.25), rgba(59,130,246,0.25))",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        {item.number}
                                    </p>
                                    <div className="flex items-center gap-4 mb-4">
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                            style={{
                                                background: "linear-gradient(135deg, #9333ea, #3b82f6)",
                                                boxShadow: "0 6px 24px rgba(147,51,234,0.45)",
                                            }}
                                        >
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-white font-bold text-xl md:text-2xl">{item.title}</h3>
                                    </div>
                                    <div className="w-12 h-0.5 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 mb-5" />
                                    <p className="text-base leading-relaxed mb-7" style={{ color: "rgba(255,255,255,0.55)" }}>
                                        {item.description}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {item.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="tag-pill px-4 py-1.5 rounded-full text-xs font-semibold"
                                                style={{
                                                    background: "rgba(147,51,234,0.1)",
                                                    border: "1px solid rgba(147,51,234,0.22)",
                                                    color: "rgba(192,132,252,0.85)",
                                                }}
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2 mt-8">
                                        {services.map((_, di) => (
                                            <button
                                                key={di}
                                                onClick={() => setActive(di)}
                                                className="h-1.5 rounded-full transition-all duration-300"
                                                style={{
                                                    width: active === di ? "2rem" : "0.375rem",
                                                    background: active === di
                                                        ? "linear-gradient(90deg, #9333ea, #3b82f6)"
                                                        : "rgba(255,255,255,0.15)",
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </section>
    );
}